import React from "react";

export type NavTheme = "transparent" | "solid";

export type NavLayout = "default" | "condensed";

type ContextProps = {
  navLayout: NavLayout;
  setNavLayout: React.Dispatch<React.SetStateAction<NavLayout>>;
  navTheme: NavTheme;
  setNavTheme: React.Dispatch<React.SetStateAction<NavTheme>>;
  /** Page-level "highlighted" mode (CMS): light header/footer chrome. */
  isPageHighlighted: boolean;
  isSubNavVisible: boolean;
  setIsSubNavVisible: React.Dispatch<React.SetStateAction<boolean>>;
  isAnnouncementBarVisible: boolean;
  setIsAnnouncementBarVisible: React.Dispatch<React.SetStateAction<boolean>>;
  activeDropdown: string | null;
  setActiveDropdown: React.Dispatch<React.SetStateAction<string | null>>;
  dropdownHeight: number;
  setDropdownHeight: React.Dispatch<React.SetStateAction<number>>;
};

const Context = React.createContext<ContextProps | undefined>(undefined);

type ProviderProps = {
  children: React.ReactNode;
  theme: NavTheme;
  isPageHighlighted?: boolean;
};

export const NavLayoutProvider = (props: ProviderProps) => {
  const { theme, children, isPageHighlighted = false } = props;
  const [navTheme, setNavTheme] = React.useState<NavTheme>(theme);
  const [navLayout, setNavLayout] = React.useState<NavLayout>("default");
  const [isSubNavVisible, setIsSubNavVisible] = React.useState(false);
  const [isAnnouncementBarVisible, setIsAnnouncementBarVisible] = React.useState(false);
  const [activeDropdown, setActiveDropdown] = React.useState<string | null>(null);
  const [dropdownHeight, setDropdownHeight] = React.useState(0);

  const value = {
    navTheme,
    setNavTheme,
    isPageHighlighted,
    navLayout,
    setNavLayout,
    isSubNavVisible,
    setIsSubNavVisible,
    isAnnouncementBarVisible,
    setIsAnnouncementBarVisible,
    activeDropdown,
    setActiveDropdown,
    dropdownHeight,
    setDropdownHeight,
  };

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useNavLayout = (): ContextProps => {
  const context = React.useContext(Context);

  if (context === undefined) {
    throw new Error("useNavLayout must be used within a NavLayoutProvider");
  }

  return context;
};
